import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui';
import { academyService } from '@/services/academy';
import type { Academy } from '@court-sync/shared';
import type { PublicOrganizationProfile } from '@/services/publicOrg';
import { useOrgPaths } from '@/hooks/useOrgPaths';
import { useOrgLoginDialogOptional } from '@/contexts/OrgLoginDialogContext';
import type { OrgRegisterMode } from '@/contexts/OrgRegisterDialogContext';
import { AcademyGuestRegistrationBlock } from '@/pages/public/publicBooking/AcademyGuestRegistrationBlock';
import { userFormSchema, type UserFormData } from '@/pages/public/publicBooking/schemas';
import { submitAcademyRegister } from '@/pages/public/academyAuthHandlers';
import { submitClubRegister } from '@/pages/public/clubAuthHandlers';
import { buildOrgEmailVerificationPath } from '@/lib/orgPaths';
import { isStarterMemberAuthBlockedAcademy } from '@/utils/publicBrowseMode';
import { BrowserImage } from '@/components/ui/BrowserImage';

interface OrgRegisterDialogProps {
  profile: PublicOrganizationProfile;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  returnUrl: string;
  mode: OrgRegisterMode;
}

export function OrgRegisterDialog({
  profile,
  open,
  onOpenChange,
  returnUrl,
  mode,
}: OrgRegisterDialogProps) {
  const navigate = useNavigate();
  const orgLogin = useOrgLoginDialogOptional();
  const { p } = useOrgPaths(profile.organization);
  const orgSlug = profile.organization.slug;

  const [academy, setAcademy] = useState<Academy | null>(null);
  const [loadingAcademy, setLoadingAcademy] = useState(false);
  const [registerSubmitting, setRegisterSubmitting] = useState(false);

  const registerForm = useForm<UserFormData>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      phone: '',
      termsAccepted: false,
      privacyAccepted: false,
      marketingEmailConsent: false,
    },
  });

  useEffect(() => {
    if (!open) {
      registerForm.reset();
      return undefined;
    }
    if (mode !== 'academy' || !profile.academySlug) {
      setAcademy(null);
      return undefined;
    }
    let cancelled = false;
    setLoadingAcademy(true);
    academyService
      .getAcademyBySlug(profile.academySlug)
      .then((a) => {
        if (!cancelled) setAcademy(a);
      })
      .catch(() => {
        if (!cancelled) setAcademy(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingAcademy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, mode, profile.academySlug, registerForm]);

  const isOrgScopedReturnUrl = (url: string) => {
    if (!orgSlug || !p) return false;
    if (url.startsWith(`/club-academy/${orgSlug}`)) return true;
    const base = p('');
    return url === base || url.startsWith(`${base}/`);
  };

  const handleRegisterSubmit = async (data: UserFormData) => {
    const destination =
      returnUrl && isOrgScopedReturnUrl(returnUrl) ? returnUrl : (p?.('/dashboard') ?? '/dashboard');

    setRegisterSubmitting(true);
    try {
      if (mode === 'academy' && profile.modules.hasAcademy && academy?.id && profile.academySlug) {
        const result = await submitAcademyRegister({
          data,
          academyId: academy.id,
          academySlug: profile.academySlug,
          dashboardReturnPath: destination,
        });

        if (result.type === 'verification') {
          onOpenChange(false);
          const orgFields = {
            slug: org.slug,
            subdomain: org.subdomain,
            subdomainEnabled: org.subdomainEnabled,
          };
          navigate(
            buildOrgEmailVerificationPath(orgFields, {
              email: result.email,
              returnUrl: result.returnUrl,
              academySlug: result.academySlug,
            }),
            {
              state: result.verificationEmailFailed ? { verificationEmailSendFailed: true } : undefined,
            }
          );
          return;
        }

        if (result.type === 'login') {
          toast.error(result.message);
          onOpenChange(false);
          orgLogin?.openOrgLogin(destination, { prefillEmail: result.email });
          return;
        }

        toast.error(result.message);
        return;
      }

      const clubResult = await submitClubRegister({ data, returnPath: destination });
      if (clubResult.type === 'success') {
        onOpenChange(false);
        if (clubResult.verificationEmailFailed) {
          toast.error('Account created, but we could not send the verification email. You can resend it when you sign in.');
        } else {
          toast.success('Account created! Continue with membership — verify your email next time you sign in.');
        }
        navigate(clubResult.returnUrl);
        return;
      }

      if (clubResult.type === 'login') {
        toast.error(clubResult.message);
        onOpenChange(false);
        orgLogin?.openOrgLogin(destination, { prefillEmail: clubResult.email });
        return;
      }

      toast.error(clubResult.message);
    } finally {
      setRegisterSubmitting(false);
    }
  };

  const org = profile.organization;
  const starterBlocked = Boolean(academy && isStarterMemberAuthBlockedAcademy(academy));
  const usesAcademySignup = mode === 'academy' && profile.modules.hasAcademy;
  const subtitle = usesAcademySignup
    ? 'Create your account to book lessons and manage schedules'
    : 'Create your account to join events, hire courts, and manage membership';

  const openSignIn = () => {
    onOpenChange(false);
    orgLogin?.openOrgLogin(returnUrl);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-md gap-0 overflow-y-auto p-0 sm:max-w-md">
        <DialogHeader className="space-y-3 border-b border-border px-6 py-5 text-center">
          <div className="flex flex-col items-center gap-2">
            {org.logoUrl ? (
              <BrowserImage
                src={org.logoUrl}
                alt={org.name}
                className="h-12 w-auto max-w-[180px] object-contain"
                referrerPolicy="no-referrer"
              />
            ) : (
              <p className="text-base font-semibold text-foreground">{org.name}</p>
            )}
          </div>
          <div className="space-y-1">
            <DialogTitle>Create account</DialogTitle>
            <DialogDescription>{subtitle}</DialogDescription>
          </div>
        </DialogHeader>

        {loadingAcademy && usesAcademySignup ? (
          <div className="px-6 py-8 text-sm text-muted-foreground">Loading…</div>
        ) : starterBlocked && usesAcademySignup ? (
          <div className="space-y-4 px-6 py-6 text-sm text-muted-foreground">
            Student and guardian registration is not available on this academy&apos;s plan.
          </div>
        ) : (
          <div className="px-6 py-5">
            <AcademyGuestRegistrationBlock
              registerSubmitting={registerSubmitting}
              form={registerForm}
              onEmailSubmit={handleRegisterSubmit}
              submitButtonLabel="Create account"
              defaultCountrySource={{
                country: org.country ?? academy?.country,
                region: (org.region ?? academy?.region) as Academy['region'],
              }}
            />
          </div>
        )}

        <DialogFooter className="flex-col gap-3 border-t border-border px-6 py-4 sm:flex-col sm:space-x-0">
          <p className="text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <button
              type="button"
              className="font-medium text-primary hover:underline"
              onClick={openSignIn}
            >
              Sign in
            </button>
          </p>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
